import axios from "axios";

// Types
export type AIProvider = 'openai' | 'claude';

export interface ChatSession {
  id: string;
  sessionId?: string; // backwards compat
  title: string | null;
  createdAt: string;
  updatedAt?: string;
  messageCount?: number;
}

export interface ToolCall {
  name: string;
  arguments?: Record<string, any>;
  result?: any;
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system" | "tool";
  content: string;
  createdAt?: string;
  timestamp?: string;
  toolCallId?: string;
  toolCalls?: ToolCall[];
}

export interface SendMessageResponse {
  sessionId: string;
  response: string;
  messages: ChatMessage[];
  provider: AIProvider;
}

export interface HealthCheckResponse {
  status: string;
  openai: boolean;
  openaiModel: string;
  claude: boolean;
  claudeModel: string;
  webhook: boolean;
  activeSessions: number;
}

export interface ToolAccessResponse {
  canRead: boolean;
  canWrite: boolean;
  tools: string[];
}

// API Functions

/**
 * Health check - verify AI assistant is operational
 */
export const healthCheck = async (): Promise<HealthCheckResponse> => {
  try {
    const res = await axios.get("/ai-assistant/health");
    return res.data;
  } catch (e: any) {
    console.error("AI Assistant health check failed:", e);
    throw new Error(e?.response?.data?.message || "AI Assistant unavailable");
  }
};

/**
 * Get available tools for current user
 */
export const getAvailableTools = async (): Promise<ToolAccessResponse> => {
  try {
    const res = await axios.get("/ai-assistant/tools");
    return res.data;
  } catch (e: any) {
    console.error("Failed to get available tools:", e);
    throw new Error(e?.response?.data?.message || "Failed to get tools");
  }
};

/**
 * Create a new chat session
 */
export const createSession = async (): Promise<ChatSession> => {
  try {
    const res = await axios.post("/ai-assistant/sessions");
    return {
      id: res.data.sessionId,
      sessionId: res.data.sessionId,
      title: null,
      createdAt: res.data.createdAt,
    };
  } catch (e: any) {
    console.error("Failed to create session:", e);
    throw new Error(e?.response?.data?.message || "Failed to create chat session");
  }
};

/**
 * List user's chat sessions
 */
export const listSessions = async (): Promise<ChatSession[]> => {
  try {
    const res = await axios.get("/ai-assistant/sessions");
    return res.data;
  } catch (e: any) {
    console.error("Failed to list sessions:", e);
    throw new Error(e?.response?.data?.message || "Failed to list chat sessions");
  }
};

/**
 * Get session history
 */
export const getSessionHistory = async (sessionId: string): Promise<{ sessionId: string; messages: ChatMessage[] }> => {
  try {
    const res = await axios.get(`/ai-assistant/sessions/${sessionId}`);
    return res.data;
  } catch (e: any) {
    console.error("Failed to get session history:", e);
    throw new Error(e?.response?.data?.message || "Failed to get chat history");
  }
};

/**
 * Update session title
 */
export const updateSessionTitle = async (sessionId: string, title: string): Promise<void> => {
  try {
    await axios.patch(`/ai-assistant/sessions/${sessionId}`, { title });
  } catch (e: any) {
    console.error("Failed to update session:", e);
    throw new Error(e?.response?.data?.message || "Failed to update chat session");
  }
};

/**
 * Delete a session
 */
export const deleteSession = async (sessionId: string): Promise<void> => {
  try {
    await axios.delete(`/ai-assistant/sessions/${sessionId}`);
  } catch (e: any) {
    console.error("Failed to delete session:", e);
    throw new Error(e?.response?.data?.message || "Failed to delete chat session");
  }
};

/**
 * Send a message to the AI assistant (non-streaming)
 */
export const sendMessage = async (
  sessionId: string | undefined,
  message: string,
  history?: ChatMessage[],
  provider: AIProvider = 'openai',
  model?: string
): Promise<SendMessageResponse> => {
  try {
    const res = await axios.post("/ai-assistant/chat", {
      sessionId,
      message,
      history,
      provider,
      model,
    });
    return res.data;
  } catch (e: any) {
    console.error("Failed to send message:", e);
    throw new Error(e?.response?.data?.message || "Failed to send message");
  }
};

/**
 * Send a message with SSE streaming
 * Returns an async generator that yields chunks
 */
export async function* streamMessage(
  sessionId: string | undefined,
  message: string,
  history?: ChatMessage[],
  provider: AIProvider = 'openai'
): AsyncGenerator<{ type: string; data: any }> {
  const response = await fetch(`${axios.defaults.baseURL}/ai-assistant/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': axios.defaults.headers.common['Authorization'] as string,
    },
    body: JSON.stringify({
      sessionId,
      message,
      history,
      provider,
    }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      
      // Process complete SSE events
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            yield data;
          } catch (e: any) {
            console.warn('Failed to parse SSE data:', line);
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Quick query - one-off question without session management
 */
export const quickQuery = async (question: string, provider: AIProvider = 'openai'): Promise<{ response: string; provider: AIProvider }> => {
  try {
    const res = await axios.post("/ai-assistant/query", { question, provider });
    return res.data;
  } catch (e: any) {
    console.error("Failed to query:", e);
    throw new Error(e?.response?.data?.message || "Failed to get answer");
  }
};
